import { FormModelFieldSubForm, type FormModelField } from '@ikegps/core-shared';
import { Icon } from '../../components/icon';
import { MaterialIcon } from '../../components/icon/icons';
import { SearchInput } from '../../components/search.input/search.input';
import { T, Translate } from '../../i18n';
import { m, type MithrilVNode } from '../../mithril';
import { FormDesignerPalette } from '../form.designer/form.palette';
import { SubformEditorDropdown } from '../form.designer/editor/subform/form.field.editor.subform.dropdown';
import { MapFieldsState } from './collection.form.migrate.map.fields.state';
import { CSS } from './collection.form.migrate.map.fields.style';
import { fieldGroupPath, fieldLabel, fieldMeta } from './collection.form.migrate.map.fields.util';

export interface MapFieldsViewProps {
	state: MapFieldsState;
}

function view(props: MapFieldsViewProps): MithrilVNode {
	const { state } = props;

	return m('div', { className: CSS.panel }, [
		m(
			'div',
			{ className: CSS.search },
			SearchInput.view({
				value: state.searchQuery,
				placeholder: Translate(T.COLLECTION_MIGRATE_FORM_SEARCH_FIELDS_PLACEHOLDER),
				onValue: (query) => state.setSearchQuery(query),
			})
		),
		m('div', { className: CSS.columnHeaders }, [
			m(
				'span',
				{ className: CSS.columnHeader },
				Translate(T.COLLECTION_MIGRATE_FORM_SOURCE_COLUMN, { formName: state.sourceForm.displayName })
			),
			m(
				'span',
				{ className: CSS.columnHeader },
				Translate(T.COLLECTION_MIGRATE_FORM_TARGET_COLUMN, { formName: state.targetForm.displayName })
			),
		]),
		m('div', { className: CSS.scroll }, viewFields(state.sourceForm.fields, state, false)),
	]);
}

/** `forceShow` is set once an ancestor subform's own name has matched the search query, revealing its
 *  whole subtree regardless of each descendant's own name — otherwise a subform is shown only when it
 *  has at least one matching descendant (recursively), and non-matching siblings inside it stay hidden. */
function viewFields(fields: FormModelField[], state: MapFieldsState, forceShow: boolean): MithrilVNode[] {
	return fields
		.map((field) => {
			const selfMatches = forceShow || state.matchesSearchQuery(field);

			if (FormModelFieldSubForm.isSubForm(field)) {
				const children = viewFields(field.fields, state, selfMatches);
				if (!selfMatches && children.length === 0) {
					return null;
				}

				return m('details', { className: CSS.group, open: true }, [
					m('summary', { className: CSS.groupHeader }, viewRow(field, state)),
					m('div', { className: CSS.groupBody }, children),
				]);
			}

			return selfMatches ? viewRow(field, state) : null;
		})
		.filter((node): node is MithrilVNode => node != null);
}

function viewRow(field: FormModelField, state: MapFieldsState): MithrilVNode {
	const isGroup = FormModelFieldSubForm.isSubForm(field);

	return m('div', { className: CSS.row }, [
		m('div', { className: CSS.rowLeft }, [
			m('div', { className: CSS.fieldInfo }, [
				isGroup ? Icon.view(MaterialIcon.KEY_RIGHT, { className: CSS.groupChevron }) : null,
				FormDesignerPalette.viewIcon(field.type, { className: CSS.fieldIcon }),
				m('div', { className: CSS.fieldText }, [
					m('div', { className: CSS.fieldName }, fieldLabel(field)),
					m('div', { className: CSS.fieldMeta }, fieldMeta(field)),
				]),
			]),
			isGroup ? viewGroupLostBadge(field, state) : viewDropdownLossBadge(field, state),
		]),
		viewTargetBox(field, state),
	]);
}

function viewGroupLostBadge(subform: FormModelFieldSubForm, state: MapFieldsState): MithrilVNode | null {
	const count = state.countLostFieldsIn(subform);

	if (count === 0) {
		return null;
	}

	return m('div', { className: CSS.groupLostBadge }, [
		Icon.view(MaterialIcon.WARNING, { className: CSS.groupLostBadgeIcon }),
		Translate(T.COLLECTION_MIGRATE_FORM_GROUP_FIELDS_LOST, { count }),
	]);
}

function viewDropdownLossBadge(field: FormModelField, state: MapFieldsState): MithrilVNode | null {
	const loss = state.getDropdownLossFor(field);

	if (loss == null || loss.distinctValues.length === 0) {
		return null;
	}

	return m('div', { className: CSS.dropdownLossBadge }, [
		Icon.view(MaterialIcon.WARNING, { className: CSS.dropdownLossBadgeIcon }),
		Translate(T.COLLECTION_MIGRATE_FORM_FIELD_DROPDOWN_VALUES_LOST, {
			fieldType: FormDesignerPalette.translate(field.type),
			count: loss.distinctValues.length,
		}),
	]);
}

function viewTargetBox(sourceField: FormModelField, state: MapFieldsState): MithrilVNode {
	const targetField = state.getTargetField(sourceField);
	const isUnmapped = targetField == null;

	return m('div', { className: `${CSS.targetBox} ${isUnmapped ? CSS.targetBoxEmpty : ''}` }, [
		SubformEditorDropdown.view({
			title: '',
			selectedText: isUnmapped
				? Translate(T.COLLECTION_MIGRATE_FORM_NO_MATCH)
				: viewTargetLabel(targetField, state.isAutoMatched(sourceField)),
			icon: isUnmapped
				? Icon.view(MaterialIcon.WARNING, { className: CSS.targetEmptyIcon })
				: FormDesignerPalette.viewIcon(targetField.type, { className: CSS.targetIcon }),
			menu: state.getMenu(sourceField),
			allowNone: true,
			noneSelected: isUnmapped,
			onNoneSelect: () => state.setTargetField(sourceField, null),
			noneItemView: viewLeaveUnmappedItem(),
			nonePosition: 'bottom',
		}),
	]);
}

function viewTargetLabel(targetField: FormModelField, isAutoMatched: boolean): MithrilVNode {
	return m('span', { className: CSS.targetLabel }, [
		m('span', { className: CSS.targetLabelText }, [
			m('span', { className: CSS.targetLabelName }, fieldLabel(targetField)),
			m('span', { className: CSS.targetLabelMeta }, ` · ${fieldGroupPath(targetField)}`),
		]),
		isAutoMatched
			? m('span', { className: CSS.targetLabelAuto }, Translate(T.COLLECTION_MIGRATE_FORM_AUTO_MATCHED_SUFFIX))
			: null,
	]);
}

function viewLeaveUnmappedItem(): MithrilVNode {
	return m('div', { className: CSS.noneItem }, [
		m('div', { className: CSS.noneItemHeader }, [
			Icon.view(MaterialIcon.WARNING, { className: CSS.noneItemIcon }),
			m('span', { className: CSS.noneItemTitle }, Translate(T.COLLECTION_MIGRATE_FORM_LEAVE_UNMAPPED)),
		]),
		m('div', { className: CSS.noneItemSubtitle }, Translate(T.COLLECTION_MIGRATE_FORM_LEAVE_UNMAPPED_DESCRIPTION)),
	]);
}

export const MapFieldsView = { view };
